import * as React from 'react';
import { Box, Breadcrumbs, Link, Skeleton, Stack, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { ArrowLeft as ArrowLeftIcon } from '@phosphor-icons/react/dist/ssr/ArrowLeft';
import { CalendarBlank as CalendarBlankIcon } from '@phosphor-icons/react/dist/ssr/CalendarBlank';
import { User as UserIcon } from '@phosphor-icons/react/dist/ssr/User';
import { Trash as TrashIcon } from '@phosphor-icons/react/dist/ssr/Trash';
import { PencilSimple as PencilSimpleIcon } from '@phosphor-icons/react/dist/ssr/PencilSimple';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { Meeting } from '@/types/meeting';
import { paths } from '@/paths';
import { toTitleCase } from '@/utils/to-title-case';
import { dayjs } from '@/lib/v2/dayjs';
import { deleteMeeting } from '@/api/meetings';
import { UserContext } from '@/contexts/auth/user-context';
import { COMMUNITY_ROLES } from '@/constants/community-roles';
import { ACCOUNT_ROLES } from '@/constants/account-roles';
import { EditMeetingDialog } from './edit-meeting-dialog';

interface MeetingHeaderProps {
  meeting?: Meeting | null;
  isLoading: boolean;
  error: Error | null;
}

export function MeetingHeader({ meeting, isLoading, error }: MeetingHeaderProps): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userContext = React.useContext(UserContext);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);

  const isFutureMeeting = React.useMemo(() => {
    if (!meeting?.held_on) return true; // Unscheduled — allow editing
    return dayjs(meeting.held_on).isAfter(dayjs());
  }, [meeting?.held_on]);

  const canEditOrDelete = React.useMemo(() => {
    if (!userContext?.user) return false;

    const accountRoleNames = (userContext.user.account_roles ?? []).map((r) => r.name);
    const communityRoleNames = (userContext.user.community_roles ?? []).map((r) => r.name);

    const isAccountAdmin = accountRoleNames.includes(ACCOUNT_ROLES.ACCOUNT_ADMIN) ||
      accountRoleNames.includes(ACCOUNT_ROLES.ACCOUNT_OWNER) ||
      accountRoleNames.includes(ACCOUNT_ROLES.ACCOUNT_MANAGER);

    const isBoardMember = communityRoleNames.includes(COMMUNITY_ROLES.BOARD_MEMBER);

    return isAccountAdmin || isBoardMember;
  }, [userContext?.user]);

  const deleteMeetingMutation = useMutation({
    mutationFn: async () => {
      if (!meeting?.id && !meeting?.uuid) throw new Error('Meeting ID not found');
      await deleteMeeting(meeting.uuid ?? meeting.id);
    },
    onSuccess: () => {
      toast.success('Meeting deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      navigate(paths.meetings.index);
    },
    onError: () => {
      toast.error('Failed to delete meeting');
    },
  });

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    deleteMeetingMutation.mutate();
  };

  const title =
    meeting?.held_on && meeting?.meeting_type?.name
      ? `${toTitleCase(meeting.meeting_type.name)} — ${dayjs(meeting.held_on).format('MMM D, YYYY')}`
      : meeting?.display_id
        ? `Meeting ${meeting.display_id}`
        : meeting?.id
          ? `Meeting #${String(meeting.id).slice(0, 8)}`
          : 'Meeting';

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton height={20} width={200} variant="text" />
        <Skeleton height={36} width={400} variant="text" />
        <Skeleton height={20} width={300} variant="text" />
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Breadcrumbs
        separator={<Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem', mx: -0.5 }}>/</Typography>}
        sx={{ fontSize: '0.8125rem' }}
      >
        <Link
          component="button"
          onClick={() => navigate(paths.meetings.index)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            textDecoration: 'none',
            fontSize: 'inherit',
            '&:hover': { color: 'text.primary' },
          }}
          underline="none"
        >
          <ArrowLeftIcon size={13} />
          Meetings
        </Link>
        <Typography color="text.primary" sx={{ fontSize: 'inherit', fontWeight: 500 }}>
          {meeting?.display_id ?? meeting?.id ?? 'Meeting'}
        </Typography>
      </Breadcrumbs>

      <Stack
        spacing={1}
        sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <Stack spacing={1} sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }} variant="h4">
            {title}
          </Typography>

          <Stack direction="row" spacing={2.5} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
            {meeting?.meeting_type?.name && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.25,
                  py: 0.25,
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                  border: '1px solid',
                  borderColor: 'primary.200',
                }}
              >
                {toTitleCase(meeting.meeting_type.name)}
              </Box>
            )}

            {meeting?.held_on && (
              <>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                  <CalendarBlankIcon size={13} />
                  <Typography variant="caption">
                    {dayjs(meeting.held_on).format('MMM D, YYYY [at] h:mm A')}
                  </Typography>
                </Stack>
              </>
            )}

            {meeting?.created_by && (
              <>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                  <UserIcon size={13} />
                  <Typography variant="caption">
                    {meeting.created_by.first_name} {meeting.created_by.last_name}
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        </Stack>

        {canEditOrDelete && isFutureMeeting && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Button
              color="primary"
              size="small"
              startIcon={<PencilSimpleIcon size={16} />}
              onClick={() => setEditDialogOpen(true)}
              variant="outlined"
            >
              Edit
            </Button>
            <Button
              color="error"
              size="small"
              startIcon={<TrashIcon size={16} />}
              onClick={handleDeleteClick}
              disabled={deleteMeetingMutation.isPending}
            >
              Delete
            </Button>
          </Stack>
        )}
      </Stack>

      {meeting && isFutureMeeting && (
        <EditMeetingDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          meeting={meeting}
        />
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Meeting</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this meeting? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteMeetingMutation.isPending}
          >
            {deleteMeetingMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
